import { useMemo, useState } from 'react';

import {
  AlertCircleIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CircleCheckIcon,
  CircleDashedIcon,
  CopyIcon,
  ExternalLinkIcon,
  HashIcon,
  MailIcon,
  PencilIcon,
  PhoneIcon,
  UserIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { LanguageFlag } from '@/components/atoms/FlagEmoji/LanguageFlag';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { StatusDot } from '@/components/ui/custom/StatusDot';
import { cn } from '@/lib/utils';
import {
  getCampaignStatusDotColor,
  getCampaignStatusLabelKey,
} from '@/modules/campaigns/components/analytics/campaignStatusBadge';
import { getChannelIcon } from '@/modules/campaigns/components/Tables/CampaignsTable/ChannelCell';
import { getNextActionShortLabel } from '@/modules/campaigns/util/nextAction';
import { selectAccountSlug } from '@/redux/session/selectors';
import type {
  Contact,
  ContactCampaignSubscriber,
  ContactIdentity,
  ContactIdentityType,
} from '@/shared/types/contacts';
import { IntegrationType } from '@/shared/types/integration';
import { getChannelKey } from '@/util/channelName';
import { formatDate, timeFromNow } from '@/util/dateUtils';
import { formatLanguageLabel } from '@/util/languageOptions';

import { getContactAvatarColor, getInitials, sourceLabelKey } from '../constants';
import { useContactCampaigns } from '../hooks/useContactCampaigns';
import { useContactConsentStatus } from '../hooks/useContactConsentStatus';
import { useContactIdentities } from '../hooks/useContactIdentities';

interface ContactSidebarProps {
  contact: Contact;
  nameLinkTo?: string;
  footer?: React.ReactNode;
  bordered?: boolean;
  onSendMessage?: () => void;
  onEdit?: () => void;
}

const CopyButton = ({ value, className }: { value: string; className?: string }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(value);
    setCopied(true);
    toast.success(t('contacts.copied_id'));
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Button
      variant="ghost-neutral"
      size="icon-sm"
      onClick={handleCopy}
      aria-label={t('contacts.copy_id')}
      className={cn('size-6', className)}
    >
      {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
    </Button>
  );
};

const identityIconMap: Record<ContactIdentityType, typeof MailIcon> = {
  email: MailIcon,
  phone: PhoneIcon,
  psid: UserIcon,
  external_id: HashIcon,
};

const enrollmentLinkPath = (
  slug: string,
  contact: Contact,
  subscriber: ContactCampaignSubscriber
) => {
  const base = `/${slug}/campaigns/${subscriber.campaign.campaign_id}/logs`;
  if (subscriber.campaign.status?.toLowerCase() === 'draft') {
    return base;
  }
  if (subscriber.campaign.type === 'payment_recovery' || subscriber.next_action) {
    return `${base}/${subscriber.subscriber_id}`;
  }
  const q = contact.email ?? contact.phone;
  return q ? `${base}?q=${encodeURIComponent(q)}` : base;
};

const conversationStatusKey = (
  subscriber: ContactCampaignSubscriber
): 'concluded' | 'in_progress' | 'no_reply' | 'not_initiated' => {
  if (subscriber.last_replied_at) {
    return 'concluded';
  }
  const reachedOut =
    !!subscriber.last_sent_at || !!subscriber.last_delivered_at || !!subscriber.last_read_at;
  const campaignStatus = subscriber.campaign.status?.toLowerCase();
  const campaignTerminal = campaignStatus === 'canceled' || campaignStatus === 'failed';
  if (reachedOut && campaignTerminal) {
    return 'no_reply';
  }
  if (reachedOut) {
    return 'in_progress';
  }
  return 'not_initiated';
};

const localTimeForZone = (timeZone?: string) => {
  if (!timeZone) {
    return undefined;
  }
  try {
    const time = new Intl.DateTimeFormat(undefined, {
      timeZone,
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date());
    const tzAbbrParts = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName');
    return tzAbbrParts ? `${time} (${tzAbbrParts.value})` : time;
  } catch {
    return timeZone;
  }
};

const knownChannels = new Set<string>(Object.values(IntegrationType));

const pickRecentChannel = (identities: ContactIdentity[]): string | undefined => {
  const withChannel = identities
    .filter((i) => i.channel && knownChannels.has(i.channel))
    .sort((a, b) => (b.updated || '').localeCompare(a.updated || ''));
  return withChannel[0]?.channel ?? undefined;
};

const LanguageValue = ({ code }: { code: string }) => {
  return (
    <span className="flex items-center gap-1.5">
      <LanguageFlag code={code} />
      <span>{formatLanguageLabel(code)}</span>
    </span>
  );
};

const ChannelValue = ({ channel }: { channel: string }) => {
  const { t } = useTranslation();
  const label = t(getChannelKey(channel as IntegrationType));
  return (
    <span className="flex items-center gap-1.5">
      {getChannelIcon(channel, 14)}
      <span>{label}</span>
    </span>
  );
};

const DetailRow = ({ label, value }: { label: string; value?: React.ReactNode }) => {
  if (value === undefined || value === null) {
    return null;
  }
  return (
    <div className="flex items-center gap-4">
      <span className="min-w-0 flex-1 text-sm leading-5 text-foreground-muted">{label}</span>
      <span className="w-30 shrink-0 truncate text-sm leading-5 text-foreground">{value}</span>
    </div>
  );
};

const IdentityTooltipContent = ({ identity }: { identity: ContactIdentity }) => {
  const { t } = useTranslation();
  const verifiedOn = identity.verified_at ? formatDate(identity.verified_at, 'MMM d, yyyy') : null;
  const verifiedLine = (() => {
    if (!identity.verified) {
      return null;
    }
    if (identity.verified_source && verifiedOn) {
      return t('contacts.verified_source_line', { 0: identity.verified_source, 1: verifiedOn });
    }
    if (identity.verified_source) {
      return t('contacts.verified_via', { 0: identity.verified_source });
    }
    if (verifiedOn) {
      return t('contacts.verified_on', { 0: verifiedOn });
    }
    return t('contacts.verified');
  })();

  return (
    <div className="flex min-w-56 flex-col gap-1.5 px-3 py-2">
      <div className="flex items-center justify-between gap-3 pb-1">
        <span className="truncate text-xs font-semibold">{identity.value}</span>
        <span className="shrink-0 text-xs text-text-primary-foreground/70">
          {timeFromNow(identity.updated)}
        </span>
      </div>
      {verifiedLine ? (
        <div className="flex items-center gap-2">
          <CircleCheckIcon size={12} className="shrink-0 text-successful" />
          <span className="text-xs">{verifiedLine}</span>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <AlertCircleIcon size={12} className="shrink-0 text-warning" />
          <span className="text-xs">{t('contacts.not_verified')}</span>
        </div>
      )}
      {identity.channel && knownChannels.has(identity.channel) && (
        <div className="flex items-center gap-2">
          <span className="flex shrink-0 items-center">{getChannelIcon(identity.channel, 12)}</span>
          <span className="text-xs">{t(getChannelKey(identity.channel as IntegrationType))}</span>
        </div>
      )}
      {!identity.wrong_party && (
        <div className="flex items-center gap-2">
          <UserIcon size={12} className="shrink-0 text-text-primary-foreground/70" />
          <span className="text-xs">{t('contacts.matches_contact')}</span>
        </div>
      )}
    </div>
  );
};

const IdentityRow = ({ identity }: { identity: ContactIdentity }) => {
  const Icon = identityIconMap[identity.type] ?? UserIcon;
  const hasKnownChannel = !!identity.channel && knownChannels.has(identity.channel);
  const leadingIcon = hasKnownChannel ? (
    <span className="flex shrink-0 items-center">{getChannelIcon(identity.channel!, 14)}</span>
  ) : (
    <Icon size={14} className="shrink-0" />
  );

  const row = (
    <div
      className={cn(
        'group flex items-center gap-2 rounded px-2 py-1.5 transition-colors hover:bg-bg-muted-50',
        !identity.verified && 'text-foreground-muted'
      )}
    >
      {leadingIcon}
      <span className="min-w-0 flex-1 truncate text-sm leading-5">{identity.value}</span>
      {identity.verified ? (
        <CircleCheckIcon size={16} className="shrink-0 text-successful" />
      ) : (
        <CircleDashedIcon size={16} className="shrink-0 opacity-40" />
      )}
    </div>
  );

  return (
    <InfoTooltip
      content={<IdentityTooltipContent identity={identity} />}
      variant="rich"
      size="auto"
      side="left"
      showArrow={false}
    >
      {row}
    </InfoTooltip>
  );
};

const CampaignRow = ({
  subscriber,
  to,
  timeZone,
}: {
  subscriber: ContactCampaignSubscriber;
  to: string | null;
  timeZone?: string;
}) => {
  const { t } = useTranslation();
  const status = subscriber.campaign.status;
  const dotColor = getCampaignStatusDotColor(status);
  const typeLabelKey =
    subscriber.campaign.type === 'payment_recovery'
      ? 'campaigns.setup.type_payment_recovery'
      : 'campaigns.setup.type_outreach';
  const isDraft = status?.toLowerCase() === 'draft';
  const createdAgo = timeFromNow(subscriber.campaign.created);
  const timeLine = isDraft
    ? t('contacts.campaign.created_ago', { 0: createdAgo })
    : t('contacts.campaign.launched_ago', { 0: createdAgo });
  const conversationKey = conversationStatusKey(subscriber);
  const getConversationLine = () => {
    if (conversationKey === 'concluded') {
      return t('contacts.campaign.conversation_concluded', {
        0: timeFromNow(subscriber.last_replied_at ?? subscriber.updated),
      });
    }
    if (conversationKey === 'in_progress') {
      return t('contacts.campaign.conversation_in_progress');
    }
    if (conversationKey === 'no_reply') {
      return t('contacts.campaign.conversation_no_reply');
    }
    return t('contacts.campaign.conversation_not_initiated');
  };
  const conversationLine = getConversationLine();
  const nextAction = subscriber.next_action;
  const nextActionLine =
    nextAction && nextAction.state !== 'finished'
      ? t('contacts.campaign.next_action', { 0: getNextActionShortLabel(t, nextAction, timeZone) })
      : undefined;

  const card = (
    <div className="group flex flex-col gap-0.5 rounded px-2 py-1.5 transition-colors hover:bg-highlight-background">
      <div className="flex items-center gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className="truncate text-sm leading-5 font-medium text-foreground">
            {subscriber.campaign.name}
          </span>
          {to && (
            <ExternalLinkIcon
              size={16}
              className="shrink-0 text-foreground-muted opacity-0 transition-opacity group-hover:opacity-100"
            />
          )}
        </div>
        <StatusDot
          color={dotColor}
          size="sm"
          tooltip={status ? t(getCampaignStatusLabelKey(status)) : undefined}
        />
      </div>
      <div className="flex flex-col gap-1">
        <div className="text-xs leading-4 text-foreground-muted">
          {isDraft ? t(typeLabelKey) : `${t(typeLabelKey)} · ${timeLine}`}
        </div>
        {!isDraft && (
          <div className="text-xs leading-4 text-foreground-muted">{conversationLine}</div>
        )}
        {!isDraft && nextActionLine && (
          <div className="text-xs leading-4 text-foreground-muted">{nextActionLine}</div>
        )}
      </div>
    </div>
  );

  if (to) {
    return (
      <Link to={to} className="block">
        {card}
      </Link>
    );
  }
  return card;
};

export const ContactSidebar = ({
  contact,
  nameLinkTo,
  footer,
  bordered = true,
  onSendMessage,
  onEdit,
}: ContactSidebarProps) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const { identities } = useContactIdentities(contact.contact_id);
  const { subscribers: campaignSubscribers } = useContactCampaigns(contact.contact_id);
  const [showMore, setShowMore] = useState(false);

  const location = [contact.location?.city, contact.location?.country].filter(Boolean).join(', ');
  const displayName = contact.name || t('contacts.unnamed');
  const localTime = localTimeForZone(contact.time_zone);
  const subtitle = [location, localTime].filter(Boolean).join(' · ');

  const recentChannel = useMemo(() => pickRecentChannel(identities), [identities]);

  const hasReachableIdentity = identities.some(
    (identity) => identity.type === 'phone' || identity.type === 'email'
  );

  // Aggregated over every reachable identity, per-transport, revoked-wins.
  const { consentStatus } = useContactConsentStatus(contact.contact_id, identities);

  return (
    <div
      className={cn(
        'flex min-h-0 flex-1 flex-col',
        bordered && 'overflow-hidden rounded-lg border border-stroke'
      )}
    >
      {/* Scrollable body */}
      <div
        className={cn(
          'scrollbar-hide flex flex-1 flex-col gap-6 overflow-y-auto',
          bordered && 'px-2 pt-2 pb-6'
        )}
      >
        {/* Centered header with gradient */}
        <div className="flex flex-col items-center rounded-md bg-gradient-to-b from-bg-info-soft from-25% to-background py-4">
          <div className="flex flex-col items-center gap-1 py-2">
            <div className="pb-2">
              <Avatar className="size-10">
                <AvatarFallback
                  className={cn(
                    'text-primary-foreground',
                    getContactAvatarColor(contact.contact_id)
                  )}
                >
                  {getInitials(contact.name)}
                </AvatarFallback>
              </Avatar>
            </div>
            <div className="flex items-center gap-1">
              <h2 className="text-lg leading-none font-semibold tracking-tight text-foreground">
                {nameLinkTo ? (
                  <Link
                    to={nameLinkTo}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex max-w-full items-center gap-1.5 hover:text-informational"
                  >
                    <span className="truncate">{displayName}</span>
                    <ExternalLinkIcon size={16} className="shrink-0" />
                  </Link>
                ) : (
                  <span className="block truncate">{displayName}</span>
                )}
              </h2>
            </div>
            {subtitle && (
              <p className="text-center text-sm leading-5 text-foreground-muted">{subtitle}</p>
            )}
          </div>
          <div className="flex gap-2 p-2">
            <InfoTooltip content={t('contacts.message')}>
              <span>
                <Button
                  variant="outline-neutral"
                  size="icon-sm"
                  onClick={onSendMessage}
                  disabled={!onSendMessage || !hasReachableIdentity}
                  aria-label={t('contacts.message')}
                >
                  <MailIcon size={16} />
                </Button>
              </span>
            </InfoTooltip>
            <InfoTooltip content={t('contacts.copy_id')}>
              <span>
                <CopyButton
                  value={contact.contact_id}
                  className="size-8 rounded-md border border-stroke bg-background hover:bg-highlight-background"
                />
              </span>
            </InfoTooltip>
            {onEdit && (
              <InfoTooltip content={t('common.edit')}>
                <span>
                  <Button
                    variant="outline-neutral"
                    size="icon-sm"
                    onClick={onEdit}
                    aria-label={t('common.edit')}
                  >
                    <PencilIcon size={16} />
                  </Button>
                </span>
              </InfoTooltip>
            )}
          </div>
        </div>

        {/* Details sections */}
        <div className="flex flex-col gap-4 px-2">
          {/* Recent activity */}
          {(() => {
            const allRows: { key: string; label: string; value: React.ReactNode }[] = [
              contact.last_replied && {
                key: 'last_replied',
                label: t('contacts.last_replied'),
                value: timeFromNow(contact.last_replied),
              },
              contact.last_contacted && {
                key: 'last_contacted',
                label: t('contacts.last_contacted'),
                value: timeFromNow(contact.last_contacted),
              },
              recentChannel && {
                key: 'recent_channel',
                label: t('contacts.recent_channel'),
                value: <ChannelValue channel={recentChannel} />,
              },
              contact.language && {
                key: 'language',
                label: t('contacts.language'),
                value: <LanguageValue code={contact.language} />,
              },
              consentStatus && {
                key: 'consent_status',
                label: t('contacts.consent_status'),
                value: (
                  <span
                    className={cn(
                      'font-semibold',
                      consentStatus === 'granted' ? 'text-text-success-firm' : 'text-destructive'
                    )}
                  >
                    {t(
                      consentStatus === 'granted'
                        ? 'contacts.consent_granted'
                        : 'contacts.consent_revoked'
                    )}
                  </span>
                ),
              },
              contact.source && {
                key: 'origin',
                label: t('contacts.origin'),
                value: t(sourceLabelKey[contact.source]),
              },
              { key: 'created', label: t('common.created'), value: timeFromNow(contact.created) },
              { key: 'updated', label: t('common.updated'), value: timeFromNow(contact.updated) },
              contact.last_seen && {
                key: 'last_seen',
                label: t('contacts.last_seen'),
                value: timeFromNow(contact.last_seen),
              },
            ].filter(Boolean) as { key: string; label: string; value: React.ReactNode }[];

            const visibleRows = showMore ? allRows : allRows.slice(0, 6);
            const hasMore = allRows.length > 6;

            return (
              <div>
                <h3 className="mb-3 text-base leading-none font-semibold text-foreground">
                  {t('contacts.recent_activity')}
                </h3>
                <div className="flex flex-col gap-4">
                  {visibleRows.map((row) => (
                    <DetailRow key={row.key} label={row.label} value={row.value} />
                  ))}
                </div>
                {hasMore && (
                  <button
                    type="button"
                    onClick={() => setShowMore((v) => !v)}
                    className="mt-3 flex items-center gap-2 text-sm font-medium text-surface-button-primary hover:underline"
                  >
                    {showMore ? t('contacts.show_less') : t('contacts.show_more')}
                    {showMore ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />}
                  </button>
                )}
              </div>
            );
          })()}

          {/* Custom fields */}
          {contact.custom_fields && Object.keys(contact.custom_fields).length > 0 && (
            <div className="pt-6">
              <h3 className="mb-3 text-base leading-none font-semibold text-foreground">
                {t('contacts.custom_fields')}
              </h3>
              <div className="flex flex-col gap-4 pl-0.5">
                {Object.entries(contact.custom_fields).map(([key, value]) => (
                  <DetailRow key={key} label={key} value={String(value ?? '')} />
                ))}
              </div>
            </div>
          )}

          {/* Contact information */}
          {identities.length > 0 && (
            <div className="pt-6">
              <h3 className="mb-3 text-base leading-none font-semibold text-foreground">
                {t('contacts.contact_information')}
              </h3>
              <div className="flex flex-col gap-1.5 px-0.5">
                {identities.map((identity) => (
                  <IdentityRow key={identity.identity_id} identity={identity} />
                ))}
              </div>
            </div>
          )}

          {/* Campaigns */}
          {campaignSubscribers.length > 0 && (
            <div className="pt-6">
              <h3 className="mb-3 text-base leading-none font-semibold text-foreground">
                {t('contacts.campaigns')}
              </h3>
              <div className="flex flex-col gap-1 pl-0.5">
                {campaignSubscribers.map((subscriber) => (
                  <CampaignRow
                    key={subscriber.subscriber_id}
                    subscriber={subscriber}
                    to={slug ? enrollmentLinkPath(slug, contact, subscriber) : null}
                    timeZone={contact.time_zone}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {footer && (
        <div className={cn('shrink-0', bordered && 'px-5 pb-5')}>
          <div className="mb-4 h-px bg-stroke" />
          {footer}
        </div>
      )}
    </div>
  );
};
